import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { FaUser, FaEnvelope, FaKey, FaCrown, FaSave, FaLock } from 'react-icons/fa';
import Footer from '../components/Layout/Footer';

export default function AccountPage() {
  const { user: jwtUser } = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get(`/user/me`);
        setUser(res.data.data);
        setName(res.data.data.userName);
        setEmail(res.data.data.email);
      } catch (err) {
        console.error(err);
      }
    };

    if (jwtUser?.id) fetchUser();
  }, [jwtUser]);

  const handleSave = async () => {
    setMessage('');
    try {
      const payload = { name, email };
      if (password) payload.password = password;

      const res = await api.put('/user/me', payload);
      setUser(res.data.data);
      setPassword('');
      setMessage('Settings updated successfully');
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || 'Failed to update settings');
    }
  };

  if (!user)
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center vh-100 p-3">
          <p className="text-muted fs-5">Loading account info...</p>
        </div>
      </DashboardLayout>
    );

  return (
    <DashboardLayout>
      <div className="container py-5">
        <h2 className="mb-4 display-6 fw-bold text-dark">Account</h2>

        {/* Global alert */}
        {message && <div className="alert alert-success">{message}</div>}

        {/* Tabs */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
            >
              Info
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'update' ? 'active' : ''}`}
              onClick={() => setActiveTab('update')}
            >
              Update
            </button>
          </li>
        </ul>

        <div className="card shadow-lg p-4 mx-auto" style={{ maxWidth: '600px' }}>
          {/* Tab 1: Info */}
          {activeTab === 'info' && (
            <div className="mb-4">
              {/* User Info */}
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 mb-4">
                <div className="bg-primary text-white p-3 rounded-circle d-flex align-items-center justify-content-center fs-2">
                  <FaUser />
                </div>
                <div className="flex-grow-1">
                  <p className="fw-semibold mb-1"> <FaUser/> {user.userName}</p>
                  <p className="text-muted mb-1 d-flex align-items-center gap-2">
                    <FaEnvelope /> {user.email}
                  </p>
                  <p className="text-muted d-flex align-items-center gap-2">
                    <FaCrown /> Plan: {user.plan || 'Free'}
                  </p>
                </div>
              </div>

              {/* API Key */}
              {user.plan === 'premium' && (
                <div className="d-flex justify-content-between align-items-center p-3 mb-4 border rounded bg-light">
                  <div className="d-flex align-items-center gap-2">
                    <FaKey className="text-secondary" />
                    <span className="font-monospace text-dark">{user.apiKey}</span>
                  </div>
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => navigator.clipboard.writeText(user.apiKey)}
                  >
                    Copy Key
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Update */}
          {activeTab === 'update' && (
            <div className="mb-4">
              <div className="mb-3">
                <label className="form-label">Full Name</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaUser />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Email</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaEnvelope />
                  </span>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">New Password</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaLock />
                  </span>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex gap-2">
                <button className="btn btn-primary d-flex align-items-center gap-2" onClick={handleSave}>
                  <FaSave /> Save Changes
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setName(user.userName);
                    setEmail(user.email);
                    setPassword('');
                    setMessage('');
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </DashboardLayout>
  );
}